import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { Navigate, Outlet, RouteProps, useLocation } from 'react-router';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { localStorageKeys, roleTypes } from './constants';
import { AuthContext } from './components/AuthContext';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import ErrorBoundary from './ErrorBoundary';

// Eager load only critical components
import Layout from './pages/Layout';
import Page400 from './pages/400';
import Page403 from './pages/403';
import Page404 from './pages/404';
import Page500 from './pages/500';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy load all other components
const Home = lazy(() => import('./pages/Home'));
const ClientIndex = lazy(() => import('./pages/ClientIndex'));
const ClientForm = lazy(() => import('./pages/Client'));
const LoanImports = lazy(() => import('./pages/LoanImports'));
const AssociationErrors = lazy(() => import('./pages/AssociationErrors'));
const ImportDocs = lazy(() => import('./pages/ImportDocs'));
const SearchLoans = lazy(() => import('./pages/SearchLoans'));
const LoanErrorIndex = lazy(() => import('./pages/LoanErrorIndex'));
const LoanErrorResolver = lazy(() => import('./pages/LoanErrorResolver'));
const SearcherRetrieval = lazy(() => import('./pages/SearcherRetrieval'));
const BatchVerificationStart = lazy(() => import('./pages/BatchVerificationStart'));
const BatchVerification = lazy(() => import('./pages/BatchVerification'));
const ClientContacts = lazy(() => import('./components/Admin/ClientContacts'));
const AdminTitleCompanies = lazy(() => import('./components/Admin/TitleCompanies'));
const AdminIndex = lazy(() => import('./pages/AdminIndex'));
const DeleteLoan = lazy(() => import('./components/Admin/DeleteLoan'));
const DeleteAssociation = lazy(() => import('./components/Admin/DeleteAssociation'));
const UndoAssociation = lazy(() => import('./components/Admin/UndoAssociation'));
const DeleteDocument = lazy(() => import('./components/Admin/DeleteDocument'));
const DownloadDocuments = lazy(() => import('./components/DownloadDocuments'));
const Reports = lazy(() => import('./pages/Reports/Index'));
const NotFoundsReport = lazy(() => import('./pages/Reports/NotFounds'));
const OutstandingLoans = lazy(() => import('./pages/Reports/OutstandingLoans'));
const NotFounds = lazy(() => import('./pages/NotFounds'));
const ReadyToShip = lazy(() => import('./pages/ReadyToShip'));
const Workflow = lazy(() => import('./pages/Workflow'));
const FlagDocument = lazy(() => import('./pages/FlagDocuments/FlaggedDocument'));
const CreateMatches = lazy(() => import('./components/Admin/CreateMatches'));
const Shipping = lazy(() => import('./pages/Shipping'));
const Document = lazy(() => import('./components/Documents/Index/Document'));
const CheckUploadedDoc = lazy(() => import('./pages/CheckUploadedDoc'));
const Shipments = lazy(() => import('./components/Shipping/Shipments'));
const ShipmentDisplay = lazy(() => import('./components/Shipping/ShipmentDisplay'));
const Investors = lazy(() => import('./components/Admin/Investors'));
const ImportNotesSpreadsheet = lazy(() => import('./pages/ImportNotesSpreadsheet'));
const TitleCompany = lazy(() => import('./pages/TitleCompany'));
const ClientLoans = lazy(() => import('./pages/ClientLoans'));
const ClientSingleLoan = lazy(() => import('./pages/ClientSingleLoan'));
const CallCenter = lazy(() => import('./pages/CallCenter'));
const DeterminationOptions = lazy(() => import('./components/Documents/Index/Determination'));
const InvestorsTable = lazy(() => import('./pages/InvestorsTable'));
const Investor = lazy(() => import('./pages/Investor'));
const CallCenterAdminOptions = lazy(() => import('./components/Admin/CallCenterAdminOptions'));
const OneOffEmail = lazy(() => import('./pages/OneOffEmails'));
const PrintCoversheets = lazy(() => import('./pages/PrintCoversheets'));
const InvestorReport = lazy(() => import('./pages/InvestorReports/InvestorReportsPage'));
const InvestorReportUpload = lazy(
  () => import('./pages/InvestorReports/StatusReportFromUpload/InvestorReportUploadPage'),
);
const InvestorReportHistory = lazy(
  () => import('./pages/InvestorReports/StatusReportFromUpload/InvestorReportHistoryPage'),
);
const InvestorReportMappings = lazy(
  () => import('./pages/InvestorReports/MappingManagement/InvestorReportMappingsPage'),
);
const InvestorAgedShipped = lazy(() => import('./pages/InvestorReports/InvestorAgedShippedPage'));
const InvestorMismatch = lazy(() => import('./pages/InvestorReports/InvestorMismatchPage'));
const DocumentRequirementReview = lazy(() => import('./pages/InvestorReports/DocumentRequirementReviewPage'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const DocumentsPageWrapper = lazy(() => import('./components/DocumentsPageWrapper'));
const DocumentIntake = lazy(() => import('./pages/reportsDashboard/TvScreens/DocumentIntake'));
const DashboardShipping = lazy(() => import('./pages/reportsDashboard/TvScreens/Shipping'));
const DashboardSorting = lazy(() => import('./pages/reportsDashboard/TvScreens/Sorting'));
const DeterminationPageWrapper = lazy(() => import('./components/DeterminationPageWrapper'));
const GeneralReporting = lazy(() => import('./pages/reportsDashboard/GeneralReporting'));
const AccountRepsPage = lazy(() => import('./components/AccountRepsPage'));
const CorrectionsBetaPage = lazy(() => import('./components/CorrectionsBeta/CorrectionsBetaPage'));
const DataAssistantPage = lazy(() => import('./components/DataAssistant/DataAssistantPage'));
const DataAssistantReviewPage = lazy(() => import('./components/DataAssistant/ReviewPage'));
const LandingPage = lazy(() => import('./pages/reportsDashboard/LandingPage'));
const DetailedShippingReport = lazy(
  () => import('./pages/reportsDashboard/DetailedShippingReport'),
);
const ClientDocumentIndex2 = lazy(() => import('./pages/ClientDocumentIndex2'));
const BulkLoansNote = lazy(() => import('./components/Admin/BulkLoansNote'));
const BulkInvestorNumber = lazy(() => import('./components/Admin/BulkInvestorNumber'));
const BulkShipDocuments = lazy(() => import('./components/Admin/BulkShipDocuments'));
const UserDashboardEventLogs = lazy(
  () => import('./pages/clientComponents/UserDashboardEventLogs'),
);
const SalesRepsDashboard = lazy(() => import('./pages/SalesRepsDashboard/SalesRepsDashboard'));
const SalesRepLandingPage = lazy(() => import('./pages/SalesRepsDashboard/LandingPage'));
const PrintingContextProvider = lazy(() =>
  import('./PrintingContext').then(m => ({ default: m.PrintingContextProvider })),
);
const AppVersion = lazy(() => import('./components/AppVersion'));
const TitleCompanyPortalLinks = lazy(() => import('./pages/TitleCompanyPortalLinks'));
const ShippingTrackingReport = lazy(() => import('./pages/Reports/ShippingTrackingReport'));
const LoansReceived = lazy(() => import('./pages/Reports/LoansReceived'));
const WaitingToBeMatchedDrillDownReport = lazy(
  () => import('./pages/reportsDashboard/WaitingToBeMatchedDrillDownReport'),
);
const WaitingToBeMatchedAsOfTodayDrillDownReport = lazy(
  () => import('./pages/reportsDashboard/WaitingToBeMatchedAsOfTodayDrillDownReport'),
);
const WaitingToBeDeterminedDrillDownReport = lazy(
  () => import('./pages/reportsDashboard/WaitingToBeDeterminedDrillDownReport'),
);
const WaitingToBeDeterminedAsOfTodayDrillDownReport = lazy(
  () => import('./pages/reportsDashboard/WaitingToBeDeterminedAsOfTodayDrillDownReport'),
);
const WaitingToBeSortedDrillDownReport = lazy(
  () => import('./pages/reportsDashboard/WaitingToBeSortedDrillDownReport'),
);
const PendingCorrectionByClient = lazy(
  () => import('./pages/reportsDashboard/PendingCorrectionByClient'),
);
const TitleCompaniesPendingAssociation = lazy(
  () => import('./pages/reportsDashboard/TitleCompaniesPendingAssociation'),
);
const InvestorsPendingAssociationByClient = lazy(
  () => import('./pages/reportsDashboard/InvestorsPendingAssociationByClient'),
);
const ClientFailures = lazy(() => import('./pages/reportsDashboard/ClientFailures'));
const CatchAll = lazy(() => import('./pages/DocumentCatchAll/Index'));
const TitlePortalLinks = lazy(() => import('./components/Admin/TitlePortalLinks'));
const DigitalDelivery = lazy(() => import('./pages/DigitalDelivery'));
const InvestorReconciliation = lazy(
  () => import('./pages/InvestorReconciliation/InvestorReconciliation'),
);
const TitleCompanies = lazy(() => import('./components/TitleCompanies'));
const Staple = lazy(() => import('./pages/Staple'));
const Sort = lazy(() => import('./pages/Sort'));
const ShipRequests = lazy(() => import('./components/Shipping/ShipRequests'));
const RevenueBreakdownReport = lazy(() => import('./pages/RevenueBreakdownReport'));
const CorrectionsPipeline = lazy(() => import('./pages/Reports/CorrectionPipeline/GenerateReport'));
const ImportDocumentNotes = lazy(() => import('./pages/ImportDocumentNotes'));
const WaitingToBeBroadMatchedDrillDownReport = lazy(
  () => import('./pages/reportsDashboard/WaitingToBeBroadMatchedDrillDownReport'),
);
const EmbedContainer = lazy(() => import('./components/PowerBI/EmbedContainer'));
const LenderAliases = lazy(() => import('./pages/LenderAliases'));
const CorrectionFulfillment = lazy(() => import('./pages/CorrectionFulfillment'));
const OutreachEscalations = lazy(() => import('./pages/OutreachEscalations'));
const ClientPortalUserManagement = lazy(() => import('./pages/ClientPortalUserManagement'));
const CorrectionCreditsReport = lazy(() => import('./pages/Reports/CorrectionCreditsReport'));
const Payments = lazy(() => import('./pages/Payments'));
const InvestorInvoices = lazy(() => import('./pages/InvestorInvoices'));
const ClosingPackages = lazy(() => import('./pages/ClosingPackages/ClosingPackages'));
const EncompassTools = lazy(() => import('./pages/EncompassTools'));
const AuditQueueByClientDrillDownReport = lazy(
  () => import('./pages/Reports/AuditQueueByClientDrillDownReport'),
);
const NewDocumentType = lazy(() => import('./pages/NewDocumentType'));
const TitlePortalOutreach = lazy(() => import('./pages/TitlePortalOutreach'));
const CollateralServices = lazy(() => import('./pages/CollateralServices/CollateralServices'));
const OriginalInternalShipDocData = lazy(() => import('./pages/OriginalInternalShipDocData'));
const CorrespondentLoanLinks = lazy(() => import('./pages/CorrespondentLoanLinks'));
const BulkNoCorrectionNeeded = lazy(() => import('./pages/BulkNoCorrectionNeeded'));
const DigitalDeliveryLogs = lazy(() => import('./pages/Reports/DigitalDeliveryLogs'));
const ChlAgingMortgagesToBeOrdered = lazy(
  () => import('./pages/Reports/ChlAgingMortgagesToBeOrdered'),
);
const ClientSatisfaction = lazy(() => import('./pages/ClientSatisfaction'));
const DocumentEventLogs = lazy(() => import('./pages/DocumentEventLogs'));
const QueueDocsForUpload = lazy(() => import('./pages/QueueDocsForUpload/QueueDocsForUpload'));

type CustomRouteProps = RouteProps & {
  protected?: boolean;
  path: string;
  component: React.ComponentType<any>;
  noLayout?: boolean;
  authorizedRoles?: string[];
  title?: string;
};

const {
  AccountRep,
  AccountsPayable,
  Admin,
  BasicUser,
  CallCenterAdmin,
  CallCenterUser,
  CallCenterUserWithShipping,
  ClientAccess,
  ClientAdmin,
  CollateralService,
  CorrectionAdmin,
  ExecutiveManagement,
  InvestorManagement,
  Data,
  DataAssociation,
  Dev,
  Digitals,
  DigitalsWithImportErrors,
  DigitalTeamLead,
  MatchAndAudit,
  MatchAndSort,
  Online,
  ProcessingAdmin,
  QCTeamLead,
  SalesManager,
  SalesRep,
  Scanner,
  ScannerWithNotFound,
  ScanningTeamLead,
  ShippingTeamLead,
  TitlePortalSupport,
} = roleTypes;

const routes: CustomRouteProps[] = [
  {
    path: '/',
    protected: true,
    title: 'Home',
    component: Home,
  },
  {
    path: '/queue-docs-for-upload',
    protected: true,
    component: QueueDocsForUpload,
    title: 'Queue Docs for Upload',
    authorizedRoles: [Admin, Dev],
  },
  {
    path: '/clients',
    protected: true,
    component: ClientIndex,
    title: 'Clients',
    authorizedRoles: [BasicUser, CallCenterAdmin, Admin, Dev, ClientAccess, AccountRep],
  },
  {
    path: '/clients/new-client',
    protected: true,
    component: ClientForm,
    title: 'Client',
    authorizedRoles: [Admin, Dev, ClientAdmin],
  },
  {
    path: '/clients/:clientId',
    protected: true,
    component: ClientLoans,
    title: 'Client',
    authorizedRoles: [Admin, BasicUser, AccountRep, CallCenterAdmin, Dev, ClientAdmin],
  },
  {
    path: '/clients/:clientId/loans',
    protected: true,
    component: ClientLoans,
    title: 'Client Loans',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev, ClientAdmin],
  },
  {
    path: '/loans/:loanId',
    protected: true,
    component: ClientSingleLoan,
    title: 'Loan',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/loan-imports',
    protected: true,
    component: LoanImports,
    title: 'Loan Imports',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Data, Admin, Dev],
  },
  {
    path: '/spreadsheets/notes',
    protected: true,
    component: ImportNotesSpreadsheet,
    title: 'Note Imports',
    authorizedRoles: [
      CallCenterAdmin,
      CallCenterUser,
      CallCenterUserWithShipping,
      BasicUser,
      AccountRep,
      Admin,
      Dev,
    ],
  },
  {
    path: '/outreach',
    protected: true,
    component: CallCenter,
    title: 'Outreach',
    authorizedRoles: [
      CallCenterAdmin,
      CallCenterUser,
      CallCenterUserWithShipping,
      BasicUser,
      AccountRep,
      Admin,
      Dev,
    ],
  },
  {
    path: '/outreach-escalations',
    protected: true,
    component: OutreachEscalations,
    title: 'Outreach Escalations',
    authorizedRoles: [
      CallCenterAdmin,
      CallCenterUser,
      CallCenterUserWithShipping,
      BasicUser,
      AccountRep,
      Admin,
      Dev,
    ],
  },
  {
    path: '/title-portal-outreach',
    protected: true,
    component: TitlePortalOutreach,
    title: 'Title Portal Outreach',
    authorizedRoles: [TitlePortalSupport, AccountRep, Admin, Dev],
  },
  {
    path: '/admin/outreach',
    protected: true,
    component: CallCenterAdminOptions,
    title: 'Outreach',
    authorizedRoles: [CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/title-companies',
    protected: true,
    component: TitleCompanies,
    title: 'Title Companies',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Admin,
      Dev,
      DataAssociation,
      TitlePortalSupport,
    ],
  },
  {
    path: '/title-companies/:titleCompanyId',
    protected: true,
    component: TitleCompany,
    title: 'Title Company',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Admin,
      Dev,
      DataAssociation,
      TitlePortalSupport,
    ],
  },
  {
    path: '/errors/associations',
    protected: true,
    component: AssociationErrors,
    title: 'Association Errors',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Data,
      Admin,
      Dev,
      DigitalsWithImportErrors,
    ],
  },
  {
    path: '/errors/loans',
    protected: true,
    component: LoanErrorIndex,
    title: 'Loan Errors',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Data,
      Admin,
      Dev,
      DigitalsWithImportErrors,
    ],
  },
  {
    path: '/errors/loans/:clientId',
    protected: true,
    component: LoanErrorResolver,
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/documents/import/physical',
    protected: true,
    component: ImportDocs,
    title: 'Document Imports - Physical',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Online,
      Admin,
      Dev,
      Digitals,
      DigitalsWithImportErrors,
    ],
  },
  {
    path: '/documents/import/digital',
    protected: true,
    component: ImportDocs,
    title: 'Document Imports - Digital',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Online,
      Admin,
      Dev,
      Digitals,
      DigitalsWithImportErrors,
    ],
  },
  {
    path: '/documents/download',
    protected: true,
    component: DownloadDocuments,
    title: 'Download Documents',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Data, Admin, Dev],
  },
  {
    path: '/documents/match',
    protected: true,
    component: NotFounds,
    title: 'Match',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Admin,
      Dev,
      MatchAndAudit,
      MatchAndSort,
      ScannerWithNotFound,
      Online,
    ],
  },
  {
    path: '/documents/readytoship',
    protected: true,
    component: ReadyToShip,
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev, MatchAndSort],
  },
  {
    path: '/sort',
    protected: true,
    component: Sort,
    title: 'Sort',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev, MatchAndSort],
  },
  {
    path: '/documents/checkuploaded',
    protected: true,
    component: CheckUploadedDoc,
    title: 'Staple (Deprecated)',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Admin,
      Dev,
      Scanner,
      ScannerWithNotFound,
    ],
  },
  {
    path: '/staple',
    protected: true,
    component: Staple,
    title: 'Staple',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Admin,
      Dev,
      Scanner,
      ScannerWithNotFound,
    ],
  },
  {
    path: '/search',
    protected: true,
    component: SearchLoans,
    title: 'Search',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/reports-dashboard/document-intake',
    protected: true,
    component: DocumentIntake,
    title: 'Document Intake Dashboard',
    authorizedRoles: [
      Admin,
      ProcessingAdmin,
      Dev,
      QCTeamLead,
      ShippingTeamLead,
      ScanningTeamLead,
      DigitalTeamLead,
    ],
    noLayout: true,
  },
  {
    path: '/reports-dashboard/shipping',
    protected: true,
    component: DashboardShipping,
    title: 'Ship Dashboard',
    authorizedRoles: [
      Admin,
      ProcessingAdmin,
      Dev,
      QCTeamLead,
      ShippingTeamLead,
      ScanningTeamLead,
      DigitalTeamLead,
    ],
    noLayout: true,
  },
  {
    path: '/reports-dashboard/sorting',
    title: 'Sort Dashboard',
    protected: true,
    component: DashboardSorting,
    authorizedRoles: [
      Admin,
      ProcessingAdmin,
      Dev,
      QCTeamLead,
      ShippingTeamLead,
      ScanningTeamLead,
      DigitalTeamLead,
    ],
    noLayout: true,
  },
  {
    path: '/reports-dashboard/landing-page',
    protected: true,
    component: LandingPage,
    title: 'Reports',
    authorizedRoles: [
      Admin,
      ProcessingAdmin,
      Dev,
      QCTeamLead,
      ShippingTeamLead,
      ScanningTeamLead,
      DigitalTeamLead,
      CorrectionAdmin,
      AccountRep,
      AccountsPayable,
    ],
  },
  {
    path: '/reports-dashboard/general-reporting',
    protected: true,
    component: GeneralReporting,
    title: 'Reports',
    authorizedRoles: [
      Admin,
      ProcessingAdmin,
      Dev,
      QCTeamLead,
      AccountRep,
      ShippingTeamLead,
      ScanningTeamLead,
      DigitalTeamLead,
      CorrectionAdmin,
      AccountsPayable,
    ],
  },
  {
    path: '/reports-dashboard/general-reporting/shipping',
    protected: true,
    component: DetailedShippingReport,
    title: 'Shipping Report',
    authorizedRoles: [Admin, ProcessingAdmin, Dev, ShippingTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/sorting',
    protected: true,
    component: WaitingToBeSortedDrillDownReport,
    title: 'Sorting Report',
    authorizedRoles: [Admin, ProcessingAdmin, Dev, QCTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/determination',
    protected: true,
    component: WaitingToBeDeterminedDrillDownReport,
    title: 'Audit Drilldown',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/determinationByClient/:clientId',
    protected: true,
    component: AuditQueueByClientDrillDownReport,
    title: 'Audit Drill Down By Client',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/determinationAsOfToday',
    protected: true,
    component: WaitingToBeDeterminedAsOfTodayDrillDownReport,
    title: 'Audit Drilldown',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/matching',
    protected: true,
    component: WaitingToBeMatchedDrillDownReport,
    title: 'Matching Drilldown',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/matchingAsOfToday',
    protected: true,
    component: WaitingToBeMatchedAsOfTodayDrillDownReport,
    title: 'Matching Drilldown',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/broadMatching',
    protected: true,
    component: WaitingToBeBroadMatchedDrillDownReport,
    title: 'Broad Matching Drilldown',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/pending-correction',
    protected: true,
    component: PendingCorrectionByClient,
    title: 'Pending Correction Report',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/title-companies-pending-association',
    protected: true,
    component: TitleCompaniesPendingAssociation,
    title: 'Title Association Report',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/investors-pending-association',
    protected: true,
    component: InvestorsPendingAssociationByClient,
    title: 'Investor Association Report',
    authorizedRoles: [Admin, Dev, DigitalTeamLead],
  },
  {
    path: '/reports-dashboard/general-reporting/client-failures',
    protected: true,
    component: ClientFailures,
    title: 'Client Failures',
    authorizedRoles: [Admin, Dev, QCTeamLead, DigitalTeamLead, ScanningTeamLead, ShippingTeamLead],
  },
  {
    path: '/workflow',
    protected: true,
    component: Workflow,
    title: 'Workflow',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/shipping',
    protected: true,
    component: Shipping,
    title: 'Shipping',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      CallCenterUserWithShipping,
      Admin,
      Dev,
    ],
  },
  {
    path: '/shipping/shipments',
    protected: true,
    component: Shipments,
    title: 'Shipments',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      CallCenterUserWithShipping,
      Admin,
      Dev,
    ],
  },
  {
    path: '/shipping/shipments/:shippingId',
    protected: true,
    component: ShipmentDisplay,
    title: 'Shipment',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      CallCenterUserWithShipping,
      Admin,
      Dev,
    ],
  },
  {
    path: '/ship-a-copy',
    protected: true,
    component: ShipRequests,
    title: 'Ship Requests',
    authorizedRoles: [Admin, Dev, ProcessingAdmin],
  },
  {
    path: '/reports',
    protected: true,
    component: Reports,
    title: 'Reports',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/reports/outstanding-loans',
    protected: true,
    component: OutstandingLoans,
    title: 'Outstanding Loans Report',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/reports/not-founds',
    protected: true,
    component: NotFoundsReport,
    title: 'Not Found Report',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/reports/shipment-tracking',
    protected: true,
    component: ShippingTrackingReport,
    title: 'Shipment Tracking Report',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/reports/correction-credits',
    protected: true,
    component: CorrectionCreditsReport,
    title: 'Correction Credits Report',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/reports/loans-received-count',
    protected: true,
    component: LoansReceived,
    title: 'Loans Received Report',
    authorizedRoles: [Admin, Dev],
  },
  {
    path: '/reports/corrections-pipeline',
    protected: true,
    component: CorrectionsPipeline,
    title: 'Corrections Report',
    authorizedRoles: [Admin, Dev, AccountRep],
  },
  {
    path: '/reports/digital-delivery-logs',
    protected: true,
    component: DigitalDeliveryLogs,
    title: 'Digital Delivery Logs',
    authorizedRoles: [
      Admin,
      ProcessingAdmin,
      Dev,
      QCTeamLead,
      ShippingTeamLead,
      ScanningTeamLead,
      DigitalTeamLead,
      CorrectionAdmin,
      AccountRep,
    ],
  },
  {
    path: '/reports/chl-aging-mortgages-to-be-ordered',
    protected: true,
    component: ChlAgingMortgagesToBeOrdered,
    title: 'CHL Aging mortgages to be ordered',
    authorizedRoles: [BasicUser, CallCenterAdmin, Admin, Dev, ClientAccess, AccountRep],
  },
  {
    path: '/searcher-retrieval',
    protected: true,
    component: SearcherRetrieval,
    title: 'Searcher Retrieval',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/one-off-email',
    protected: true,
    component: OneOffEmail,
    authorizedRoles: [Dev],
  },
  {
    path: '/batch-verification-start',
    protected: true,
    component: BatchVerificationStart,
    title: 'Batch verification',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/batch-verification/:barcode',
    protected: true,
    component: BatchVerification,
    title: 'Batch verification',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/documents',
    protected: true,
    component: Document,
    title: 'Document Index',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Admin,
      Dev,
      Scanner,
      ScannerWithNotFound,
    ],
  },
  {
    path: '/documents-old',
    protected: true,
    component: ClientDocumentIndex2,
    title: 'Document Index (Deprecated)',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Admin,
      Dev,
      Scanner,
      ScannerWithNotFound,
    ],
  },
  {
    path: '/documents/:documentId',
    protected: true,
    component: DocumentsPageWrapper,
    title: 'Document',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Online,
      Admin,
      Dev,
      Scanner,
      ScannerWithNotFound,
    ],
  },
  {
    path: '/determine-document',
    protected: true,
    component: DeterminationPageWrapper,
    title: 'Audit',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Online,
      Admin,
      Dev,
      Digitals,
      DigitalsWithImportErrors,
      MatchAndAudit,
    ],
  },
  {
    path: '/new-det-doc',
    protected: true,
    title: 'Audit',
    component: DeterminationPageWrapper,
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/new-documents/:documentId',
    protected: true,
    component: DocumentsPageWrapper,
    title: 'Document',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Online,
      Admin,
      Dev,
      Scanner,
      ScannerWithNotFound,
    ],
  },
  {
    path: '/investors',
    protected: true,
    component: InvestorsTable,
    title: 'Investors',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev, DataAssociation],
  },
  {
    path: '/investors/:investorId',
    protected: true,
    component: Investor,
    title: 'Investor',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev, DataAssociation],
  },
  {
    path: '/knowledge-base',
    protected: true,
    component: KnowledgeBase,
    title: 'Knowledge Base',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin',
    protected: true,
    component: AdminIndex,
    title: 'Admin',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Admin,
      Dev,
      ProcessingAdmin,
      ExecutiveManagement,
    ],
  },
  {
    path: '/admin/clientcontacts',
    protected: true,
    component: ClientContacts,
    title: 'Client Contacts',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin/titlecompanies',
    protected: true,
    component: AdminTitleCompanies,
    title: 'Title Companies',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin/deleteLoan',
    protected: true,
    component: DeleteLoan,
    title: 'Delete Loan',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin/deleteassociation',
    protected: true,
    component: DeleteAssociation,
    title: 'Delete Association',
    authorizedRoles: [Admin, Dev, DataAssociation],
  },
  {
    path: '/admin/undoassociation',
    protected: true,
    component: UndoAssociation,
    title: 'Undo Association',
    authorizedRoles: [Admin, Dev, DataAssociation],
  },
  {
    path: '/admin/deletedocument',
    protected: true,
    component: DeleteDocument,
    title: 'Delete Document',
    authorizedRoles: [Admin, Dev],
  },
  {
    path: '/admin/flagdocument',
    protected: true,
    component: FlagDocument,
    title: 'Flag Document',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin/creatematches',
    protected: true,
    component: CreateMatches,
    title: 'Match',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin/investors',
    protected: true,
    component: Investors,
    title: 'Investor',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin/investor-reconciliation',
    protected: true,
    component: InvestorReconciliation,
    title: 'Investor Number Reconciliation',
    authorizedRoles: [Admin, Dev, QCTeamLead],
  },
  {
    path: '/admin/bulk-loan-note',
    protected: true,
    component: BulkLoansNote,
    title: 'Bulk Loan Note',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin/bulk-ship-documents',
    protected: true,
    component: BulkShipDocuments,
    title: 'Bulk Ship',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/admin/bulk-investor-number',
    protected: true,
    component: BulkInvestorNumber,
    title: 'Bulk Investor Number',
    authorizedRoles: [Admin, Dev, QCTeamLead],
  },
  {
    path: '/admin/digital-delivery-settings',
    protected: true,
    component: DigitalDelivery,
    title: 'Digital Delivery Settings',
    authorizedRoles: [Admin, Dev, QCTeamLead],
  },
  {
    path: '/admin/revenue-breakdown-report',
    protected: true,
    component: RevenueBreakdownReport,
    title: 'Revenue Breakdown Report',
    authorizedRoles: [Admin, ExecutiveManagement, Dev],
  },
  {
    path: '/admin/payments',
    protected: true,
    component: Payments,
    title: 'Payments',
    authorizedRoles: [ExecutiveManagement, AccountsPayable, Dev],
  },
  {
    path: '/investor-invoices',
    protected: true,
    component: InvestorInvoices,
    title: 'Investor Invoices',
    authorizedRoles: [Admin, BasicUser, AccountRep, CallCenterAdmin, Dev],
  },
  {
    path: '/admin/lender-aliases',
    component: LenderAliases,
    protected: true,
    title: 'Lender Aliases',
    authorizedRoles: [Admin, Dev, QCTeamLead],
  },
  {
    path: '/admin/import-document-notes',
    protected: true,
    component: ImportDocumentNotes,
    title: 'Import Document Note',
    authorizedRoles: [AccountRep, Admin, Dev],
  },
  {
    path: '/admin/new-document-type',
    protected: true,
    component: NewDocumentType,
    title: 'New document type',
    authorizedRoles: [ExecutiveManagement, Dev],
  },
  {
    path: '/determination',
    protected: true,
    component: DeterminationOptions,
    title: 'Audit',
    authorizedRoles: [
      BasicUser,
      AccountRep,
      CallCenterAdmin,
      Online,
      Admin,
      Dev,
      Digitals,
      DigitalsWithImportErrors,
      MatchAndAudit,
    ],
  },
  {
    path: '/title-company-portal-links',
    protected: true,
    component: TitleCompanyPortalLinks,
    title: 'Title Portal Uploads',
    authorizedRoles: [Admin, Dev, BasicUser, AccountRep, CallCenterAdmin],
  },
  {
    path: '/title-portal-links',
    protected: true,
    component: TitlePortalLinks,
    title: 'Title Portal Link',
    authorizedRoles: [BasicUser, Admin, Dev, AccountRep, SalesRep],
  },
  {
    path: '/print-coversheets',
    protected: true,
    component: () => (
      <Suspense fallback={<LoadingSpinner />}>
        <PrintingContextProvider>
          <PrintCoversheets />
        </PrintingContextProvider>
      </Suspense>
    ),
    title: 'Coversheet',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/investor-reports',
    protected: false,
    title: 'Investor Report',
    component: InvestorReport,
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/investor-management/upload',
    protected: true,
    title: 'IM Upload Report',
    component: InvestorReportUpload,
    authorizedRoles: [InvestorManagement, ProcessingAdmin, Admin, Dev, ExecutiveManagement],
  },
  {
    path: '/investor-management/history',
    protected: true,
    title: 'IM Upload History',
    component: InvestorReportHistory,
    authorizedRoles: [AccountRep, Admin, Dev],
  },
  {
    path: '/investor-management/mappings',
    protected: true,
    title: 'IM Mappings',
    component: InvestorReportMappings,
    authorizedRoles: [InvestorManagement, ProcessingAdmin, Admin, Dev, ExecutiveManagement],
  },
  {
    path: '/investor-management/aged-shipped',
    protected: true,
    title: 'IM Aged Shipped',
    component: InvestorAgedShipped,
    authorizedRoles: [ProcessingAdmin, Dev, Admin, ExecutiveManagement],
  },
  {
    path: '/investor-management/investor-mismatch',
    protected: true,
    title: 'IM Investor Mismatch',
    component: InvestorMismatch,
    authorizedRoles: [ProcessingAdmin, Dev, Admin, ExecutiveManagement],
  },
  {
    path: '/investor-management/doc-require-review',
    protected: true,
    title: 'Document Requirement Review',
    component: DocumentRequirementReview,
    authorizedRoles: [AccountRep, Admin, Dev],
  },
  {
    path: '/user-dashboard/log-event',
    protected: true,
    title: 'Dashboard',
    component: UserDashboardEventLogs,
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/powerbi',
    protected: true,
    component: EmbedContainer,
    title: 'Power BI',
    authorizedRoles: [Dev, Admin, AccountRep, TitlePortalSupport],
  },
  {
    path: '/sales-reps-dashboard',
    protected: true,
    component: SalesRepsDashboard,
    title: 'Sales Rep Dashboard',
    authorizedRoles: [Dev, SalesManager, SalesRep],
  },
  {
    path: '/sales-reps-landing-page',
    protected: true,
    component: SalesRepLandingPage,
    title: 'Sales Rep Dashboard',
    authorizedRoles: [Dev, SalesManager, SalesRep],
  },
  {
    path: '/account-reps',
    protected: true,
    title: 'Corrections',
    component: () => (
      <Suspense fallback={<LoadingSpinner />}>
        <PrintingContextProvider>
          <AccountRepsPage />
        </PrintingContextProvider>
      </Suspense>
    ),
    authorizedRoles: [AccountRep, Admin, Dev],
  },
  {
    path: '/corrections-beta',
    protected: true,
    title: 'Corrections 2.0',
    component: () => (
      <Suspense fallback={<LoadingSpinner />}>
        <PrintingContextProvider>
          <CorrectionsBetaPage />
        </PrintingContextProvider>
      </Suspense>
    ),
    authorizedRoles: [AccountRep, Admin, Dev],
  },
  {
    path: '/data-assistant',
    protected: true,
    component: () => (
      <Suspense fallback={<LoadingSpinner />}>
        <DataAssistantPage />
      </Suspense>
    ),
    authorizedRoles: [Admin, Dev],
  },
  {
    path: '/data-assistant/review',
    protected: true,
    component: () => (
      <Suspense fallback={<LoadingSpinner />}>
        <DataAssistantReviewPage />
      </Suspense>
    ),
    authorizedRoles: [Admin, Dev],
  },
  {
    path: '/correction-fulfillment',
    protected: true,
    component: CorrectionFulfillment,
    title: 'Correction Fulfillment',
    authorizedRoles: [BasicUser, AccountRep, CallCenterAdmin, Admin, Dev],
  },
  {
    path: '/closing-packages',
    protected: true,
    component: ClosingPackages,
    title: 'Closing Packages',
    authorizedRoles: [BasicUser, AccountRep, Admin, Dev],
  },
  {
    path: '/collateral-services',
    protected: true,
    component: CollateralServices,
    title: 'Collateral Services',
    authorizedRoles: [CollateralService, Dev, ExecutiveManagement],
  },
  {
    path: '/document-catch-all',
    protected: true,
    component: CatchAll,
    title: 'Catch All',
    authorizedRoles: [
      Admin,
      Dev,
      BasicUser,
      DataAssociation,
      ProcessingAdmin,
      QCTeamLead,
      Digitals,
      DigitalsWithImportErrors,
      DigitalTeamLead,
      MatchAndSort,
      Online,
    ],
  },
  {
    path: '/client-satisfaction',
    protected: true,
    component: ClientSatisfaction,
    title: 'Client Satisfaction',
    authorizedRoles: [Admin, Dev, AccountRep],
  },
  {
    path: '/admin/client-portal-user-management',
    protected: true,
    component: ClientPortalUserManagement,
    title: 'Client Portal User Management',
    authorizedRoles: [ExecutiveManagement, Dev, Admin, ClientAdmin, AccountRep],
  },
  {
    path: '/admin/encompass-tools',
    protected: true,
    component: EncompassTools,
    title: 'Encompass Tools',
    authorizedRoles: [Admin, Dev, ClientAdmin],
  },
  {
    path: '/admin/original-internal-ship-doc-data',
    protected: true,
    component: OriginalInternalShipDocData,
    title: 'Internal Ship Doc Data',
    authorizedRoles: [Admin, Dev, BasicUser],
  },
  {
    path: '/admin/correspondent-loan-links',
    protected: true,
    component: CorrespondentLoanLinks,
    title: 'Correspondent Linked Loans',
    authorizedRoles: [Admin, Dev],
  },
  {
    path: '/document-event-logs',
    protected: true,
    component: DocumentEventLogs,
    title: 'Document Event Logs',
    authorizedRoles: [Dev],
  },
  {
    path: '/admin/bulk-no-correction-needed',
    protected: true,
    component: BulkNoCorrectionNeeded,
    title: 'Bulk No Correction Needed',
    authorizedRoles: [Admin, Dev],
  },
  {
    path: '/version',
    component: AppVersion,
    noLayout: true,
    authorizedRoles: [Dev],
  },
  {
    path: '/400',
    component: Page400,
    noLayout: true,
  },
  {
    path: '/403',
    component: Page403,
    noLayout: true,
  },
  {
    path: '/500',
    component: Page500,
    noLayout: true,
  },
  {
    path: '*',
    component: Page404,
    noLayout: true,
  },
];

export const routesAuthorizedRoles = routes.reduce<Map<string, string[]>>((map, route) => {
  map.set(route.path.toLowerCase(), route.authorizedRoles || []);

  return map;
}, new Map());

export default function Router() {
  const { roles } = useContext(AuthContext);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route path="/*" element={<LayoutWrapper />}>
        {routes.map(({ path, component: Component, title, ...route }) => {
          if (route.authorizedRoles && !route.authorizedRoles.some(x => roles.includes(x))) {
            return (
              <Route
                key={path}
                path={path.startsWith('/') ? path.substring(1) : path}
                element={<Navigate to="/403" />}
              />
            );
          }

          // in the api fetch wrapper, in adalConfig.ts, we check on every api GET/POST request to see if we need a reload, if we do, we reload here
          if (localStorage.getItem(localStorageKeys.IS_REFRESH_REQUIRED) === 'true') {
            window.location.reload();
          }

          return (
            <Route
              key={path}
              path={path.startsWith('/') ? path.substring(1) : path}
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <Component />
                </Suspense>
              }
            />
          );
        })}
        ,
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
}

const LayoutWrapper = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const title = routes.find(route => {
      if (!route.path.includes('/:')) {
        return route.path === pathname;
      }

      const iterationRoutePath = route.path.split('/');
      const currentLocationPath = pathname.split('/');
      if (iterationRoutePath.length !== currentLocationPath.length) {
        return false;
      }

      for (let i = 0; i < iterationRoutePath.length; i++) {
        if (iterationRoutePath[i].startsWith(':')) {
          continue;
        }

        if (iterationRoutePath[i] !== currentLocationPath[i]) {
          return false;
        }
      }

      return true;
    })?.title;

    document.title = title ? `${title} - Docprobe 3` : 'Docprobe 3';
  }, [pathname]);

  const hideSidebarRoutes = routes.filter(x => x.noLayout).map(x => x.path);
  const commandPaletteRoutes = routes
    .filter(route => route.title && !route.path.includes(':') && !route.path.includes('*'))
    .map(route => ({
      path: route.path,
      title: route.title!,
    }));

  return (
    <ErrorBoundary>
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <Layout hideSidebarRoutes={hideSidebarRoutes} commandPaletteRoutes={commandPaletteRoutes}>
          <Outlet />
        </Layout>
      </QueryParamProvider>
    </ErrorBoundary>
  );
};
